import type { CollectionSummaryJSONObject, FormSummaryJSONObject } from '@ikegps/core-api';
import { T, Translate } from '../../i18n';
import { AppStore } from '../../store';

export interface FormVersionGroup {
	formRevision: string;
	displayVersion: number | null;
	poleLabels: string[];
}

/** One distinct form, which may span more than one version when its poles disagree on version too. */
export interface FormGroup {
	displayName: string;
	versions: FormVersionGroup[];
}

export interface MixedFormsBreakdown {
	totalFormCount: number;
	totalPoleCount: number;
	/** Null when no single form/version is shared by more than one collection (nothing to treat as "the rest"). */
	primaryDisplayVersion: number | null;
	differentForm: FormGroup[];
	differentVersionSameForm: FormGroup[];
}

function groupByFormRevision(collections: CollectionSummaryJSONObject[]): Map<string, CollectionSummaryJSONObject[]> {
	const byRevision = new Map<string, CollectionSummaryJSONObject[]>();
	for (const collection of collections) {
		const group = byRevision.get(collection.formRevision);
		if (group == null) {
			byRevision.set(collection.formRevision, [collection]);
		} else {
			group.push(collection);
		}
	}
	return byRevision;
}

function unknownFormLabel(): string {
	return Translate(T.VERIFY_MIXED_FORMS_UNKNOWN_FORM);
}

function poleLabel(collection: CollectionSummaryJSONObject): string {
	return collection.title || collection.subtitle || collection.uuid;
}

function sortByVersionDescending(versions: FormVersionGroup[]): FormVersionGroup[] {
	return [...versions].sort((a, b) => (b.displayVersion ?? -1) - (a.displayVersion ?? -1));
}

/**
 * Groups a job's collections by form/form version relative to the largest (most common) form
 * revision, or null when every collection already shares the same form revision. A "baseline" is
 * only established when some form revision is shared by 2+ collections (an actual majority to
 * compare the rest against) — otherwise every collection is shown, grouped by form, since there's
 * no principled way to pick one as "the rest" (UFO-1732).
 */
export async function buildMixedFormsBreakdown(
	collections: CollectionSummaryJSONObject[]
): Promise<MixedFormsBreakdown | null> {
	const byRevision = groupByFormRevision(collections);
	if (byRevision.size <= 1) {
		return null;
	}

	const formSummaries = new Map<string, FormSummaryJSONObject | null>();
	await Promise.all(
		Array.from(byRevision.keys()).map(async (revision) => {
			formSummaries.set(revision, await AppStore.FormSummary.getAsync(revision));
		})
	);

	// Identifies "the same form": the form's uuid when known, otherwise the revision itself, so an
	// unresolved form is never assumed to match another.
	const formKey = (revision: string): string => formSummaries.get(revision)?.uuid ?? `unresolved:${revision}`;

	const revisionsByFormKey = new Map<string, string[]>();
	for (const revision of byRevision.keys()) {
		const key = formKey(revision);
		const revisions = revisionsByFormKey.get(key);
		if (revisions == null) {
			revisionsByFormKey.set(key, [revision]);
		} else {
			revisions.push(revision);
		}
	}

	let primaryRevision = '';
	let primaryCount = -1;
	for (const [revision, group] of byRevision) {
		if (group.length > primaryCount) {
			primaryCount = group.length;
			primaryRevision = revision;
		}
	}
	const hasBaseline = primaryCount >= 2;
	const primaryFormKey = hasBaseline ? formKey(primaryRevision) : null;

	const toVersionGroup = (revision: string): FormVersionGroup => ({
		formRevision: revision,
		displayVersion: formSummaries.get(revision)?.displayversion ?? null,
		poleLabels: (byRevision.get(revision) ?? []).map(poleLabel),
	});

	const differentForm: FormGroup[] = [];
	const differentVersionSameForm: FormGroup[] = [];

	for (const [key, revisions] of revisionsByFormKey) {
		const displayName = formSummaries.get(revisions[0])?.displayname ?? unknownFormLabel();

		if (hasBaseline && key === primaryFormKey) {
			const otherRevisions = revisions.filter((revision) => revision !== primaryRevision);
			if (otherRevisions.length > 0) {
				differentVersionSameForm.push({
					displayName,
					versions: sortByVersionDescending(otherRevisions.map(toVersionGroup)),
				});
			}
			continue;
		}

		differentForm.push({
			displayName,
			versions: sortByVersionDescending(revisions.map(toVersionGroup)),
		});
	}

	return {
		totalFormCount: byRevision.size,
		totalPoleCount: collections.length,
		primaryDisplayVersion: hasBaseline ? (formSummaries.get(primaryRevision)?.displayversion ?? null) : null,
		differentForm,
		differentVersionSameForm,
	};
}
